/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import createSagaMiddleware from '@redux-saga/core';
import { configureStore as _configureStore } from '@reduxjs/toolkit';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React from 'react';
import { Provider } from 'react-redux';
import { combineReducers, Store } from 'redux';
import { createLogger } from 'redux-logger';
import createRootSaga from 'redux-root-saga';

import { queryClient } from '~/apis';
import { I18nProvider } from '~/app';
import { DebugEmailVerifiedProvider } from '~/app/DebugEmailVerifiedContext';
import { AuthProvider } from '~/auth';
import { boardReducer, BoardState, cameraSaga, initialBoardState } from '~/board';
import { DebugMenu } from '~/debug';
import { gameReducer, GameState, initialGameState, spectatorSagaRoot } from '~/game';
import {
  initialOverlayState,
  LeftPanelOpenBodyClass,
  overlayReducer,
  OverlayState,
  TanstackDevtoolsPanelObserver,
  TanstackDevtoolsTooltipTitle,
} from '~/overlay';
import {
  BAR_BOTTOM_HEIGHT_PERCENT,
  FAB_EDGE_INSET_REM,
  FAB_SIZE_REM,
  FAB_SIZE_REM_MD,
  TANSTACK_DEVTOOLS_BOTTOM_OFFSET_REM,
} from '~/overlay/overlaySpacing';
import SubscriptionReturnHandler from '~/payment/SubscriptionReturnHandler';
import {
  initialState as initialSetupState,
  reducer as setupReducer,
  State as SetupState,
} from '~/setup';

import { config, DEBUG_enabled, isProductionEnvironment } from './config';
import { connectionReducer, ConnectionState, initialConnectionState } from './connectionStore';
import { env } from './env';
import { FullscreenProvider } from './FullscreenContext';
import logger from './logger';
import mainSaga from './mainSaga';

export const primary = '#026666';
export const secondary = '#f8dd64';

export interface State {
  readonly connection: ConnectionState;
  readonly game: GameState;
  readonly board: BoardState;
  readonly overlay: OverlayState;
  readonly setup: SetupState;
}

export const initialState: State = {
  connection: initialConnectionState,
  game: initialGameState,
  board: initialBoardState,
  overlay: initialOverlayState,
  setup: initialSetupState,
};

const reducer = combineReducers({
  connection: connectionReducer,
  game: gameReducer,
  board: boardReducer,
  overlay: overlayReducer,
  setup: setupReducer,
});

const sagaMiddleware = createSagaMiddleware();

const configureStore = () => {
  return _configureStore({
    reducer,
    middleware: (getDefaultMiddleware) => {
      const middlewares = getDefaultMiddleware({
        serializableCheck: {
          // Ignore these action types
          ignoredActions: ['TICK'],
        },
      });

      middlewares.push(sagaMiddleware);

      if (config.reduxLogEnabled && env.dev) {
        middlewares.push(createLogger());
      }

      return middlewares;
    },
    devTools: env.dev && {
      actionsDenylist: ['TICK'],
    },
  });
};

const initStore = (): Store => {
  const store = configureStore();
  const rootSaga = createRootSaga([mainSaga, cameraSaga, spectatorSagaRoot], {
    onError: (e) => logger.error({ err: e }, 'saga error'),
  });
  sagaMiddleware.run(rootSaga);
  return store;
};

/** Reusable provider tree for Vite entry (and legacy wrapRootElement if used elsewhere). */
export function AppProviders({ children }: { children: React.ReactNode }): JSX.Element {
  // One store per app mount; useState initializer runs once per component instance
  // so we avoid creating a new store (and a new saga tree) on every re-render.
  const [store] = React.useState(() => initStore());
  return (
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        {DEBUG_enabled && !isProductionEnvironment && (
          <>
            <ReactQueryDevtools buttonPosition="bottom-left" initialIsOpen={false} />
            <style
              data-overlay="tanstack-devtools-position"
              dangerouslySetInnerHTML={{
                __html: [
                  `.tsqd-parent-container .tsqd-open-btn-container {`,
                  `  bottom: calc(${BAR_BOTTOM_HEIGHT_PERCENT}% + ${TANSTACK_DEVTOOLS_BOTTOM_OFFSET_REM}rem) !important;`,
                  `  left: ${FAB_EDGE_INSET_REM}rem !important;`,
                  `  width: ${FAB_SIZE_REM}rem !important;`,
                  `  height: ${FAB_SIZE_REM}rem !important;`,
                  `  min-width: ${FAB_SIZE_REM}rem !important;`,
                  `  min-height: ${FAB_SIZE_REM}rem !important;`,
                  `  display: flex !important;`,
                  `  align-items: center !important;`,
                  `  justify-content: center !important;`,
                  `  opacity: 0 !important;`,
                  `  transform: translateX(-100%) !important;`,
                  `  transition: opacity 0.3s ease, transform 0.3s ease !important;`,
                  `}`,
                  `body.tsqd-devtools-button-mounted .tsqd-parent-container .tsqd-open-btn-container {`,
                  `  opacity: 1 !important;`,
                  `  transform: translateX(0) !important;`,
                  `}`,
                  `.tsqd-parent-container .tsqd-open-btn-container .tsqd-open-btn {`,
                  `  width: 100% !important;`,
                  `  height: 100% !important;`,
                  `  min-width: ${FAB_SIZE_REM}rem !important;`,
                  `  min-height: ${FAB_SIZE_REM}rem !important;`,
                  `  display: flex !important;`,
                  `  align-items: center !important;`,
                  `  justify-content: center !important;`,
                  `  padding: 0 !important;`,
                  `}`,
                  `.tsqd-parent-container .tsqd-open-btn-container .tsqd-open-btn svg {`,
                  `  width: 1.5rem !important;`,
                  `  height: 1.5rem !important;`,
                  `  flex-shrink: 0 !important;`,
                  `}`,
                  `@media (min-width: 768px) {`,
                  `  .tsqd-parent-container .tsqd-open-btn-container {`,
                  `    width: ${FAB_SIZE_REM_MD}rem !important;`,
                  `    height: ${FAB_SIZE_REM_MD}rem !important;`,
                  `    min-width: ${FAB_SIZE_REM_MD}rem !important;`,
                  `    min-height: ${FAB_SIZE_REM_MD}rem !important;`,
                  `  }`,
                  `  .tsqd-parent-container .tsqd-open-btn-container .tsqd-open-btn {`,
                  `    min-width: ${FAB_SIZE_REM_MD}rem !important;`,
                  `    min-height: ${FAB_SIZE_REM_MD}rem !important;`,
                  `  }`,
                  `  .tsqd-parent-container .tsqd-open-btn-container .tsqd-open-btn svg { width: 1.75rem !important; height: 1.75rem !important; }`,
                  `}`,
                  `body.left-panel-open .tsqd-parent-container .tsqd-open-btn-container {`,
                  `  opacity: 0 !important; pointer-events: none !important;`,
                  `  transform: translateX(-100%) !important;`,
                  `  transition: opacity 0.3s ease, transform 0.3s ease !important;`,
                  `}`,
                ].join(' '),
              }}
            />
          </>
        )}
        <Provider store={store}>
          <AuthProvider>
            <DebugEmailVerifiedProvider>
              <FullscreenProvider>
                <I18nProvider urlPath="/">
                  <>
                    {children}
                    <DebugMenu />
                    {DEBUG_enabled && !isProductionEnvironment && (
                      <>
                        <TanstackDevtoolsPanelObserver />
                        <TanstackDevtoolsTooltipTitle />
                        <LeftPanelOpenBodyClass />
                      </>
                    )}
                    <SubscriptionReturnHandler />
                  </>
                </I18nProvider>
              </FullscreenProvider>
            </DebugEmailVerifiedProvider>
          </AuthProvider>
        </Provider>
      </QueryClientProvider>
    </React.StrictMode>
  );
}

// Legacy wrapRootElement API (kept for compatibility if needed)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const wrapWithProvider = ({ element }: any): JSX.Element => {
  return <AppProviders>{element}</AppProviders>;
};

export default wrapWithProvider;
